import { useQuery } from "@tanstack/react-query";
import { api, generateQueryKey } from "@/lib/api";
import {
  buildBuyingGroupRequestParams,
  createBuyingGroupQueryKeyPart,
} from "@/lib/buying-group-utils";

export interface Activity {
  id: string;
  date: string;
  activity: string;
  channel: string;
  status: string;
  team: string;
  record_type: string;
  campaign_id?: string;
  campaign_name?: string;
  direction?: string;
  involved_team_ids?: string[];
  thread_id?: string;
  people: Array<{
    id: string;
    role_in_touchpoint?: string;
  }>;
  source_record_type?: string;
  source_record_id?: string;
  session_id?: string;
  account_id?: string; // Added to track which account an activity belongs to (for Person Explorer)
}

// Define the possible value types that can appear in the data array
type ActivityValue =
  | string
  | number
  | boolean
  | null
  | string[]
  | Array<{ id: string; role_in_touchpoint?: string }>;

// New interface for the updated API response format
export interface ActivitiesResponse {
  headers: string[];
  data: ActivityValue[][];
}

export interface ActivityTimelineData {
  activities: Array<{
    date: string;
    decayed_in_value: number;
    decayed_out_value: number;
  }>;
}

// Define buying group mode type
export type BuyingGroupMode = "default" | "opportunity" | "custom";

export function useAccountActivities(
  accountId?: string,
  opportunityIds?: string[],
  personIds?: string[],
  buyingGroupMode: BuyingGroupMode = "default",
) {
  // Use the shared utility for consistent query key generation
  const queryKey = [
    ...generateQueryKey(
      `datahub/accounts/${encodeURIComponent(accountId || "")}/activities/`,
    ),
    createBuyingGroupQueryKeyPart({
      opportunityIds,
      personIds,
      buyingGroupMode,
    }),
  ];

  return useQuery<Activity[]>({
    queryKey,
    queryFn: async () => {
      // Use the shared utility for consistent parameter building
      const params = buildBuyingGroupRequestParams({
        opportunityIds,
        personIds,
        buyingGroupMode,
      });

      const response = await api.get<ActivitiesResponse>(
        `datahub/accounts/${encodeURIComponent(accountId || "")}/activities/`,
        {
          params,
        },
      );

      // Transform the response from headers/data format to an array of objects
      const { headers, data } = response;

      return data.map((row) => {
        const activity: Partial<Activity> = {};
        headers.forEach((header, index) => {
          const value = row[index];
          (activity as Record<string, ActivityValue>)[header] = value;
        });
        return activity as Activity;
      });
    },
    enabled: !!accountId,
  });
}

export function useAccountActivityTimeline(
  accountId?: string,
  opportunityIds?: string[],
  personIds?: string[],
  buyingGroupMode: BuyingGroupMode = "default",
) {
  // Use the shared utility for consistent query key generation
  const queryKey = [
    ...generateQueryKey(
      `datahub/accounts/${encodeURIComponent(
        accountId || "",
      )}/activity-timeline/`,
    ),
    createBuyingGroupQueryKeyPart({
      opportunityIds,
      personIds,
      buyingGroupMode,
    }),
  ];

  return useQuery<ActivityTimelineData>({
    queryKey,
    queryFn: () => {
      // Use the shared utility for consistent parameter building
      const params = buildBuyingGroupRequestParams({
        opportunityIds,
        personIds,
        buyingGroupMode,
      });

      return api.get<ActivityTimelineData>(
        `datahub/accounts/${encodeURIComponent(
          accountId || "",
        )}/activity-timeline/`,
        { params },
      );
    },
    enabled: !!accountId,
  });
}
